// src/pages/SubAccountRequest.js
import React, { useState } from "react";
import TextInput from "../components/TextInput";
import TermsModal from "../components/TermsModal";
// 1) Import the API function
import { submitSubAccountRequest } from "../services/api";

// Helper function to format a name: first letter uppercase, rest lowercase.
const formatName = (name) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

function SubAccountRequest() {
  const [formData, setFormData] = useState({
    // Main User Details
    main_first_name: "",
    main_last_name: "",
    main_email: "",
    main_user_code: "",

    // Partner Account Holder Information
    partner_first_name: "",
    partner_last_name: "",
    partner_email: "",
    partner_phone_number: "",
    address_line_1: "",
    address_line_2: "",
    address_city: "",
    postcode: "",
    county: "",

    // Terms and Conditions for Partner Accounts (Booleans)
    consent_requirement: false,
    allow_email_creation: false,
    // We have split the single compliance text into two separate fields:
    compliance_agreement: false,
    compliance_agreement_2: false,

    // Terms acceptance
    terms_accepted: false,
  });

  // State to control the modal display
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle text or checkbox changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Modal functions
  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Called when user accepts T&C in the modal
  const acceptTerms = () => {
    setFormData((prev) => ({ ...prev, terms_accepted: true }));
  };

  // Submit handler with validation
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required text fields for Main User Details and Partner Account Holder Information
    const requiredTextFields = [
      "main_first_name",
      "main_last_name",
      "main_email",
      "main_user_code",
      "partner_first_name",
      "partner_last_name",
      "partner_email",
      "partner_phone_number",
      "address_line_1",
      "address_line_2",
      "address_city",
      "postcode",
      "county",
    ];

    for (const field of requiredTextFields) {
      if (!formData[field].trim()) {
        alert(`Please fill out the "${field}" field.`);
        return;
      }
    }

    // Check Terms & Conditions acceptance
    if (!formData.terms_accepted) {
      alert("You must accept the Terms & Conditions!");
      return;
    }

    console.log("Submitting Partner Account Request form:", formData);

    try {
      // 1) Call the API to submit the Partner Account Request
      const result = await submitSubAccountRequest(formData);

      // Extract the registration id returned from the API.
      const registrationId = result.id;

      // 2) Now generate the consent link with the registration id and the new email fields included.
      const consentLink =
        `https://forms.ttanalytics.net/subaccount/consent` +
        `?registration_id=${registrationId}` +
        `&partner_first_name=${encodeURIComponent(formData.partner_first_name)}` +
        `&partner_last_name=${encodeURIComponent(formData.partner_last_name)}` +
        `&main_first_name=${encodeURIComponent(formData.main_first_name)}` +
        `&main_last_name=${encodeURIComponent(formData.main_last_name)}` +
        `&partner_email=${encodeURIComponent(formData.partner_email)}` +
        `&main_email=${encodeURIComponent(formData.main_email)}` +
        `&main_user_code=${encodeURIComponent(formData.main_user_code)}`;

      // 3) Display the generated consent link
      alert(
        `Request submitted successfully! ID: ${registrationId}\n\n` +
          `Please provide this link to the partner so they can complete the Partner Account Consent:\n\n${consentLink}`
      );

      // Optionally reset the form or navigate to a success page.
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed. Please try again or contact support.");
    }
  };

  // Prepare formatted partner full name for display in the second checkbox
  const formattedPartnerName =
    formatName(formData.partner_first_name) + " " + formatName(formData.partner_last_name) + " ";

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto" }}>
      <h1>Partner Account Request</h1>
      <form onSubmit={handleSubmit}>
        {/* ---- Main User Details ---- */}
        <div style={{ marginBottom: "1rem" }}>
          <strong>Main User Details</strong>
        </div>
        <TextInput
          label="Main First Name (required)"
          name="main_first_name"
          value={formData.main_first_name}
          onChange={handleChange}
        />
        <TextInput
          label="Main Last Name (required)"
          name="main_last_name"
          value={formData.main_last_name}
          onChange={handleChange}
        />
        <TextInput
          label="Main Email (required)"
          name="main_email"
          value={formData.main_email}
          onChange={handleChange}
          type="email"
        />
        <TextInput
          label="Main User Code (required)"
          name="main_user_code"
          value={formData.main_user_code}
          onChange={handleChange}
        />

        {/* ---- Partner Account Holder Information ---- */}
        <div style={{ margin: "1rem 0 0.5rem" }}>
          <strong>Partner Account Holder Information</strong>
        </div>
        <TextInput
          label="Partner First Name (required)"
          name="partner_first_name"
          value={formData.partner_first_name}
          onChange={handleChange}
        />
        <TextInput
          label="Partner Last Name (required)"
          name="partner_last_name"
          value={formData.partner_last_name}
          onChange={handleChange}
        />
        <TextInput
          label="Partner Email (required)"
          name="partner_email"
          value={formData.partner_email}
          onChange={handleChange}
          type="email"
        />
        <TextInput
          label="Partner Phone Number (required)"
          name="partner_phone_number"
          value={formData.partner_phone_number}
          onChange={handleChange}
          type="tel"
        />
        <TextInput
          label="Address Line 1 (required)"
          name="address_line_1"
          value={formData.address_line_1}
          onChange={handleChange}
        />
        <TextInput
          label="Address Line 2 (required)"
          name="address_line_2"
          value={formData.address_line_2}
          onChange={handleChange}
        />
        <TextInput
          label="City (required)"
          name="address_city"
          value={formData.address_city}
          onChange={handleChange}
        />
        <TextInput
          label="Postcode (required)"
          name="postcode"
          value={formData.postcode}
          onChange={handleChange}
        />
        <TextInput
          label="County (required)"
          name="county"
          value={formData.county}
          onChange={handleChange}
        />

        {/* ---- Terms and Conditions for Partner Account Request ---- */}
        <div style={{ margin: "1rem 0 0.5rem" }}>
          <strong>Terms and Conditions for Partner Account Request</strong>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="consent_requirement"
              checked={formData.consent_requirement}
              onChange={handleChange}
            />
            &nbsp;<strong>Consent Requirement:</strong> TT International will contact the
            Partner Account holder via email to obtain their explicit consent before adding
            them as a Partner Account.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="allow_email_creation"
              checked={formData.allow_email_creation}
              onChange={handleChange}
            />
            &nbsp;<strong>Email Creation:</strong> Once we receive consent, the main user
            can create emails for the Partner Account.
          </label>
        </div>

        {/* ---- Split the compliance into two separate checkboxes ---- */}
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="compliance_agreement"
              checked={formData.compliance_agreement}
              onChange={handleChange}
            />
            &nbsp;<strong>Compliance (Part 1):</strong> The Partner Account holder must
            agree to the terms outlined in the Partner Account Consent Form and abide by
            all rules on ticket purchasing limits and platform guidelines.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="compliance_agreement_2"
              checked={formData.compliance_agreement_2}
              onChange={handleChange}
            />
            &nbsp;<strong>Compliance (Part 2):</strong> I confirm that {formattedPartnerName}
            has given their explicit consent to be added as a Partner Account holder under
            my account and has agreed to TT International's Partner Account Consent Form.
          </label>
        </div>

        {/* ---- Terms Acceptance (via Modal) ---- */}
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="terms_accepted"
              checked={formData.terms_accepted}
              onChange={() => {}}
              disabled
            />
            &nbsp;I have read and accept the Terms & Conditions (required)
          </label>
          &nbsp;
          <button onClick={openModal} style={{ marginLeft: "6px" }}>
            View T&C & Privacy
          </button>
        </div>

        <button type="submit">Submit Request</button>
      </form>

      {/* Terms & Privacy Modal */}
      {isModalOpen && <TermsModal onClose={closeModal} onAccept={acceptTerms} />}
    </div>
  );
}

export default SubAccountRequest;
